/** Client-safe builtin-tools constants ported from ai-foundation. */

export const CORE_PLATFORM_MCP_FAMILIES = [
  'user_memory',
  'artifacts',
  'project_files',
  'app_mcp',
] as const;

export type CoreFamilyId = (typeof CORE_PLATFORM_MCP_FAMILIES)[number];

export const KNOWN_BUILTIN_TOOLS = [
  'Bash',
  'Read',
  'Write',
  'Edit',
  'Glob',
  'Grep',
  'NotebookEdit',
  'WebFetch',
  'WebSearch',
  'Task',
  'Skill',
  'workflow_start',
] as const;

export const SDK_PRESET_EXTRA_TOOL_NAMES = [
  'TodoWrite',
  'ExitPlanMode',
  'AskUserQuestion',
] as const;

export const ADMIN_PANEL_BUILTIN_TOOL_NAMES = [
  ...KNOWN_BUILTIN_TOOLS,
  ...SDK_PRESET_EXTRA_TOOL_NAMES,
] as const;

export const PLATFORM_FLOOR_SAFETY_TOOLS = ['Bash', 'Write', 'Edit'] as const;

export type PlatformToolCategory = {
  id: string;
  name: string;
  description: string;
  order: number;
};

export const PLATFORM_TOOL_CATEGORIES: readonly PlatformToolCategory[] = [
  {
    id: 'platform.skills',
    name: 'Skills',
    description:
      "Manage the user's skills — kebab-case markdown procedures the agent loads as plugin instructions. CRUD + GitHub install.",
    order: 10,
  },
  {
    id: 'platform.projects',
    name: 'Projects',
    description:
      "Manage the user's projects — named workspaces that scope chats, memories, files, and per-project defaults.",
    order: 20,
  },
  {
    id: 'platform.documents',
    name: 'Documents',
    description:
      "Manage the user's documents — rich-text pages in the workspace editor. CRUD + field updates.",
    order: 25,
  },
  {
    id: 'platform.library',
    name: 'Library',
    description:
      "Manage the user's upload-folder library — create, rename, move, delete, and list folders that organize uploaded files.",
    order: 27,
  },
  {
    id: 'platform.memory',
    name: 'Memory',
    description:
      "Manage the user's durable memories — facts the agent carries across conversations. Create with a review form before saving.",
    order: 30,
  },
  {
    id: 'platform.workflow',
    name: 'Workflows',
    description:
      'Discover available workflow definitions via workflow_list_definitions, then start, signal, poll, list, and cancel durable runs — multi-step automation that survives restarts.',
    order: 35,
  },
];

export type AppMcpSubfamilyId = (typeof PLATFORM_TOOL_CATEGORIES)[number]['id'];

export function categoriesInRenderOrder(): readonly PlatformToolCategory[] {
  return [...PLATFORM_TOOL_CATEGORIES].sort(
    (a, b) => a.order - b.order || a.id.localeCompare(b.id),
  );
}

export const APP_MCP_SUBFAMILY_BOUNDARY_NOTES: Partial<Record<AppMcpSubfamilyId, string>> = {
  'platform.documents':
    'Project editor loses agent editing while disabled (user editing is unaffected).',
  'platform.workflow': 'In-flight runs are not cancelled — exposure-only disable.',
};

export type BuiltinToolsSeed = {
  alwaysOn: string[];
  alwaysOff: string[];
  coreFamiliesDisabled: CoreFamilyId[];
  appMcpSubfamiliesDisabled: AppMcpSubfamilyId[];
  appMcpSubfamilyToolCounts?: Record<string, number>;
};

export type FloorTier = 'alwaysOn' | 'default' | 'alwaysOff';

export type BuiltinToolsPersistPayload = {
  alwaysOn: string[];
  alwaysOff: string[];
  coreFamiliesDisabled: string[];
  appMcpSubfamiliesDisabled: string[];
};

export function buildPersistPayload(args: {
  alwaysOn: Set<string>;
  alwaysOff: Set<string>;
  coreFamiliesDisabled: Set<CoreFamilyId>;
  appMcpSubfamiliesDisabled: Set<AppMcpSubfamilyId>;
}): BuiltinToolsPersistPayload {
  return {
    alwaysOn: [...args.alwaysOn].sort(),
    alwaysOff: [...args.alwaysOff].sort(),
    coreFamiliesDisabled: [...args.coreFamiliesDisabled].sort(),
    appMcpSubfamiliesDisabled: [...args.appMcpSubfamiliesDisabled].sort(),
  };
}

export const ROW_COPY: Record<string, { label: string; description: string }> = {
  Bash: {
    label: 'Shell commands',
    description:
      'Anything from a directory listing to a destructive rm. Wide blast radius.',
  },
  Read: {
    label: 'Read files',
    description: 'Open files in the workspace.',
  },
  Write: {
    label: 'Create / overwrite files',
    description:
      'Make new files or clobber existing ones. Affects your working tree.',
  },
  Edit: {
    label: 'Edit files',
    description:
      'Modify existing files. Same blast radius as Write — wrong path, wrong file.',
  },
  Glob: {
    label: 'Glob (find files by pattern)',
    description: 'Read-only filesystem search by name.',
  },
  Grep: {
    label: 'Grep (find text in files)',
    description: 'Read-only filesystem search by content.',
  },
  NotebookEdit: {
    label: 'Edit Jupyter notebooks',
    description: 'Modify .ipynb cells in the workspace.',
  },
  WebFetch: {
    label: 'Fetch from the web',
    description:
      'Network egress to an arbitrary URL. Lower destructive risk but a data-exfil vector.',
  },
  WebSearch: {
    label: 'Web search',
    description:
      'Query a public search index. Requires WEB_SEARCH_ENABLED on the deployment; alwaysOn cannot enable it when the env gate is off.',
  },
  Task: {
    label: 'Spawn a sub-agent',
    description: 'Delegate a sub-task to a fresh agent with its own tool budget.',
  },
  TodoWrite: {
    label: 'Todo write',
    description: 'Track in-session todos.',
  },
  ExitPlanMode: {
    label: 'Exit plan mode',
    description: 'Leave research/plan orchestration.',
  },
  AskUserQuestion: {
    label: 'Ask user question',
    description: 'Structured user prompts.',
  },
};

export const CORE_PLATFORM_FAMILY_LABELS: Record<CoreFamilyId, string> = {
  user_memory: 'User Memory',
  artifacts: 'Artifacts',
  project_files: 'Project Files',
  app_mcp: 'App MCP',
};

export const CORE_FAMILY_IMPACT: Record<
  CoreFamilyId,
  { summary: string; breaks: string[] }
> = {
  user_memory: {
    summary:
      'Disabling User Memory makes the agent forget how to remember — for every user.',
    breaks: [
      'Memory recall disappears from every conversation (the agent no longer sees saved memories).',
      "The save tools (`add_memory`, `search_memory`, and App MCP's `createMemory`) are removed.",
      'Automatic memory capture after each turn stops.',
      "Existing memories are kept and stay visible in Settings — they're just invisible to the agent until you re-enable.",
    ],
  },
  artifacts: {
    summary:
      "Disabling Artifacts removes the agent's ability to create, edit, or show documents, dashboards, and code files.",
    breaks: [
      '`view`, `create_file`, `str_replace`, and `present_files` are removed — no new artifacts, no edits to existing ones.',
      'In-progress artifact workflows fail mid-conversation.',
      "Existing artifacts stay viewable in the UI; the agent just can't touch them.",
    ],
  },
  project_files: {
    summary:
      'Disabling Project Files blinds agents to every file attached to a project.',
    breaks: [
      '`read_project_file` is removed.',
      "The file manifest disappears from the agent's context — it won't know the files exist.",
      'Files stay stored and editable in project settings.',
    ],
  },
  app_mcp: {
    summary:
      'Disabling App MCP removes EVERY first-party platform tool — the widest blast radius of the four.',
    breaks: [
      'All agent-driven create/edit across the app stops: projects, skills, documents and the editor, memory CRUD.',
      'Agent-mediated forms and overlays open but the agent can no longer fill or submit them.',
      'The agent falls back to chat-only behaviour platform-wide.',
    ],
  },
};

export const PRESET_IMPACT: Record<string, string> = {
  Bash: "Denies all shell execution platform-wide — code running, package installs, file conversion, and the sandbox shell all stop for every user. Thread-level 'Allow' cannot override a platform deny.",
  Write:
    'The agent can no longer create files in the sandbox — artifacts pipelines and skills that write output will fail.',
  Edit: 'The agent can no longer modify files in the sandbox — iterative code/document editing breaks mid-task.',
};

export function presetImpactCopy(toolName: string): string {
  return (
    PRESET_IMPACT[toolName] ??
    `Removes \`${toolName}\` for every user; thread-level 'Allow' cannot override a platform deny.`
  );
}

export function tierForTool(
  toolName: string,
  alwaysOn: Set<string>,
  alwaysOff: Set<string>,
): FloorTier {
  if (alwaysOn.has(toolName)) return 'alwaysOn';
  if (alwaysOff.has(toolName)) return 'alwaysOff';
  return 'default';
}
